/**
 * Devises proposées à l'utilisateur. Les montants restent des entiers en unités de la devise
 * (pas de centimes) : la devise ne change que l'affichage. Noms et symboles viennent d'Intl, en
 * français, avec les usages locaux pour l'Afrique (FCFA, DH, DT, DA…).
 * Même table que `supabase/functions/_shared/messages.ts` (rédaction des notifications).
 */

export const DEFAULT_CURRENCY = 'XOF'

/** Symboles d'usage courant qu'Intl ne donne pas (il écrit « F CFA », « MAD »…). */
const LOCAL_SYMBOLS: Readonly<Record<string, string>> = {
  XOF: 'FCFA',
  XAF: 'FCFA',
  MAD: 'DH',
  TND: 'DT',
  DZD: 'DA',
  GNF: 'FG',
  CDF: 'FC',
  KMF: 'FC',
  MRU: 'UM',
  LYD: 'LD',
  EGP: 'E£',
  NGN: '₦',
  GHS: 'GH₵',
  KES: 'KSh',
  ZAR: 'R',
}

/** Devises les plus utilisées par nos utilisateurs : en tête de liste. */
export const POPULAR_CURRENCIES = [
  'XOF',
  'XAF',
  'EUR',
  'USD',
  'MAD',
  'DZD',
  'TND',
  'NGN',
  'GHS',
  'GNF',
  'CDF',
  'KES',
  'ZAR',
  'GBP',
  'CAD',
  'CHF',
  'AED',
  'SAR',
] as const

/** Liste de repli si le navigateur ne fournit pas `Intl.supportedValuesOf`. */
const FALLBACK = [...POPULAR_CURRENCIES, 'EGP', 'MRU', 'KMF', 'LYD', 'RWF', 'UGX', 'TZS', 'ETB']

export function isCurrencyCode(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z]{3}$/.test(value)
}

/** Toutes les devises connues : populaires d'abord, puis les autres par ordre alphabétique. */
export function currencyCodes(): string[] {
  let all: string[] = FALLBACK
  try {
    all = Intl.supportedValuesOf('currency')
  } catch {
    // Navigateur ancien : liste de repli.
  }
  const popular = new Set<string>(POPULAR_CURRENCIES)
  return [...POPULAR_CURRENCIES, ...all.filter((code) => !popular.has(code)).sort()]
}

const symbols = new Map<string, string>()

/** « FCFA », « € », « $US », « DH »… ; le code lui-même si rien de mieux n'existe. */
export function currencySymbol(code: string): string {
  const local = LOCAL_SYMBOLS[code]
  if (local) return local
  const cached = symbols.get(code)
  if (cached) return cached
  let symbol = code
  try {
    symbol =
      new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'symbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? code
  } catch {
    // Code inconnu d'Intl : on affiche le code.
  }
  symbols.set(code, symbol)
  return symbol
}

/** « Franc CFA (BCEAO) », « Euro », « Dirham marocain »… */
export function currencyName(code: string): string {
  try {
    const name = new Intl.DisplayNames('fr', { type: 'currency' }).of(code) ?? code
    return name.charAt(0).toLocaleUpperCase('fr-FR') + name.slice(1)
  } catch {
    return code
  }
}

/** Devise lue par les lecteurs d'écran : « francs CFA », « euro », « dirham marocain »… */
export function currencySpoken(code: string): string {
  if (code === 'XOF' || code === 'XAF') return 'francs CFA'
  return currencyName(code).toLocaleLowerCase('fr-FR')
}

/** Devise la plus probable selon le fuseau de l'appareil (proposée, toujours modifiable). */
const ZONE_CURRENCY: Readonly<Record<string, string>> = {
  'Africa/Porto-Novo': 'XOF',
  'Africa/Abidjan': 'XOF',
  'Africa/Dakar': 'XOF',
  'Africa/Lome': 'XOF',
  'Africa/Ouagadougou': 'XOF',
  'Africa/Bamako': 'XOF',
  'Africa/Niamey': 'XOF',
  'Africa/Bissau': 'XOF',
  'Africa/Douala': 'XAF',
  'Africa/Libreville': 'XAF',
  'Africa/Malabo': 'XAF',
  'Africa/Brazzaville': 'XAF',
  'Africa/Bangui': 'XAF',
  'Africa/Ndjamena': 'XAF',
  'Africa/Casablanca': 'MAD',
  'Africa/Algiers': 'DZD',
  'Africa/Tunis': 'TND',
  'Africa/Lagos': 'NGN',
  'Africa/Accra': 'GHS',
  'Africa/Conakry': 'GNF',
  'Africa/Kinshasa': 'CDF',
  'Africa/Lubumbashi': 'CDF',
  'Africa/Nairobi': 'KES',
  'Africa/Johannesburg': 'ZAR',
  'America/New_York': 'USD',
  'America/Chicago': 'USD',
  'America/Los_Angeles': 'USD',
  'America/Toronto': 'CAD',
  'America/Montreal': 'CAD',
  'Europe/London': 'GBP',
  'Europe/Zurich': 'CHF',
}

export function currencyForTimeZone(timeZone: string): string {
  const known = ZONE_CURRENCY[timeZone]
  if (known) return known
  return timeZone.startsWith('Europe/') ? 'EUR' : DEFAULT_CURRENCY
}

let source: () => string = () => DEFAULT_CURRENCY

/**
 * Branche la devise active (celle du profil). L'application fournit une fonction qui lit un état
 * réactif : tout affichage qui formate un montant se met alors à jour dès que la devise change.
 */
export function setCurrencySource(read: () => string): void {
  source = read
}

export function activeCurrency(): string {
  const code = source()
  return isCurrencyCode(code) ? code : DEFAULT_CURRENCY
}
